import {
  BorrowerOnboardingDetails,
  CheckoutSession,
  Currency,
  CustomTheme,
  EmploymentStatus,
  Income,
  NetIncomeType,
  OnboardingDetailsRole,
  PhoneNumber,
} from '@nemuru-eng/js-api-client';
import ibanLib from 'iban';
import { v4 as uuidv4 } from 'uuid';

import { CheckoutSessionCustomerData } from '@/components/modules/Application/types/CheckoutSessionCustomerData';
import { StoredBorrowerOnboardingDetails } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/types';
import {
  mapNieDataToFormValues,
  NIE_DATA_FORM_BLANK_VALUES,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/_shared/constants/nieDataFormBlankValues';
import { mapFormValuesToStoredNieData } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/_shared/maps/nieDataForm.map';
import { BorrowerOnboardingDetailsFormType } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/schemas/borrowerOnboardingDetailsForm.schema';
import { seniorityDateFormatter } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/ProfessionalDetailsSection/utils/seniorityDateToMonthsAndYears';
import { ADDRESS_BLANK_VALUES } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/constants/AddressBlankValues';
import { AddressMap } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/maps/address.map';
import { EmptyDialCodeOption } from '@/components/shared/components/NewContextForms/PhoneFormDoubleField/constants/EmptyDialCodeOption';
import { getDialCodeItemFromList } from '@/components/shared/components/NewContextForms/PhoneFormDoubleField/utils/getDialCodeItemFromList';

export class BorrowerOnboardingDetailsMap {
  static fromFormToStored(
    formValues: BorrowerOnboardingDetailsFormType,
    role: OnboardingDetailsRole,
    currency: Currency,
    themeName: string,
    isAutoLoan: boolean,
  ): StoredBorrowerOnboardingDetails {
    const monthlyNetIncome = Number(formValues.monthlyNetIncome);
    const anualNetIncome = formValues.numberOfYearlyPayments * Number(formValues.monthlyNetIncome);
    const isSelfEmployed = formValues.employmentStatus === EmploymentStatus.SELF_EMPLOYED;
    const shouldSendSelfEmploymentNetIncome = isSelfEmployed && isAutoLoan && themeName !== CustomTheme.CAIXABANK;
    const nieData = mapFormValuesToStoredNieData(formValues);

    return {
      createdAt: new Date(),
      id: uuidv4(),
      role,
      relation: formValues.relation,
      email: formValues.email,
      phoneNumber: `+${formValues.dialCode.dialCode}${formValues.phoneNumber}`,
      userDetails: {
        documentId: {
          type: formValues.documentType,
          expeditor: formValues.documentExpeditor,
          nationality: formValues.nationality,
          expiration: new Date(formValues.expiration),
          documentNumber: formValues.documentNumber as string,
          name: {
            first: formValues.firstName,
            last: formValues.lastName,
          },
          birthDate: new Date(formValues.birthDate as string),
          gender: formValues.gender,
          address: AddressMap.toDto(formValues.documentAddress),
          birthPlaceAddress: formValues.birthPlace,
        },
        familiarSituationDetails: {
          marital: {
            status: formValues.maritalStatus,
            regime: formValues.maritalRegime ?? undefined,
          },
          childSupportNumber: formValues.childSupportNumber as number,
        },
        mainResidenceDetails: {
          address: AddressMap.toDto(formValues.mainResidenceAddress),
          ownershipDetails: {
            type: formValues.ownershipType,
            propertyType: formValues.propertyType ?? undefined,
            mortgage: formValues.mortgage === undefined ? undefined : formValues.mortgage === 'true' ? true : false,
          },
          homeExpenses: {
            currency,
            amount: parseFloat(formValues.homeExpenses),
          },
          entranceDate: new Date(formValues.entranceDate),
        },
        professionalDetails: {
          employmentStatus: formValues.employmentStatus,
          professionalPosition: formValues.professionalPosition ?? undefined,
          selfEmploymentNetIncome: shouldSendSelfEmploymentNetIncome
            ? {
                value: { amount: anualNetIncome, currency },
                date: new Date(`${getSelfEmploymentNetIncomeYear()}-12-31`),
              }
            : undefined,
          companyInfo:
            !formValues.companyActivity || !formValues.companyName || !formValues.companyCity || !formValues.seniority
              ? undefined
              : {
                  activity: formValues.companyActivity,
                  name: formValues.companyName,
                  city: formValues.companyCity,
                  seniority: {
                    months: seniorityDateFormatter.dateToMonthsAndYears(formValues.seniority)?.seniorityMonths ?? 0,
                    years: seniorityDateFormatter.dateToMonthsAndYears(formValues.seniority)?.seniorityYears ?? 0,
                  },
                  contract: formValues.contractDateEnd ? { dateEnd: formValues.contractDateEnd as Date } : undefined,
                  ...(!formValues.corporateIdNumber ? {} : { corporateIdNumber: formValues.corporateIdNumber }),
                },
        },
      },
      income: {
        ...(role === OnboardingDetailsRole.BORROWER
          ? {
              accountNumber: ibanLib.printFormat(formValues.bankAccountNumber, ''),
            }
          : {}),
        netIncome: {
          type: formValues.netIncomeType,
          monthly: {
            currency,
            amount: monthlyNetIncome,
          },
          annual: {
            currency,
            amount: anualNetIncome,
          },
        },
      },
      ...(nieData ? { nieData } : {}),
    };
  }

  static fromStoredToForm(data: StoredBorrowerOnboardingDetails): BorrowerOnboardingDetailsToForm {
    const seniority = data.userDetails.professionalDetails?.companyInfo?.seniority as
      | undefined
      | { months: number; years: number };

    const phoneNumber = new PhoneNumber(data.phoneNumber);

    return {
      email: data.email,
      dialCode: getDialCodeItemFromList(phoneNumber.countryCallingCode) ?? EmptyDialCodeOption,
      phoneNumber: phoneNumber.nationalNumber,
      relation: data.relation,
      documentType: data.userDetails.documentId.type,
      documentExpeditor: data.userDetails.documentId.expeditor,
      nationality: data.userDetails.documentId.nationality,
      expiration: new Date(data.userDetails.documentId.expiration),
      documentNumber: data.userDetails.documentId.documentNumber,
      documentAddress: AddressMap.fromRepositoryToForm(data.userDetails.documentId.address),
      firstName: data.userDetails.documentId.name.first,
      lastName: data.userDetails.documentId.name.last,
      birthDate: new Date(data.userDetails.documentId.birthDate),
      gender: data.userDetails.documentId.gender,
      birthPlace: data.userDetails.documentId.birthPlaceAddress,
      maritalStatus: data.userDetails.familiarSituationDetails.marital.status,
      maritalRegime: data.userDetails.familiarSituationDetails.marital.regime,
      childSupportNumber: data.userDetails.familiarSituationDetails.childSupportNumber ?? 0,
      ownershipType: data.userDetails.mainResidenceDetails.ownershipDetails.type,
      propertyType: data.userDetails.mainResidenceDetails.ownershipDetails.propertyType,
      mortgage: data.userDetails.mainResidenceDetails.ownershipDetails.mortgage === true ? 'true' : 'false',
      homeExpenses: String(data.userDetails.mainResidenceDetails.homeExpenses.amount),
      ...(data.userDetails.mainResidenceDetails.entranceDate
        ? { entranceDate: new Date(data.userDetails.mainResidenceDetails.entranceDate) }
        : {}),
      mainResidenceAddress: AddressMap.fromRepositoryToForm(data.userDetails.mainResidenceDetails.address),
      employmentStatus: data.userDetails.professionalDetails?.employmentStatus,
      corporateIdNumber: data.userDetails.professionalDetails?.companyInfo?.corporateIdNumber ?? undefined,
      selfEmploymentNetIncomeDate: data.userDetails.professionalDetails?.selfEmploymentNetIncome?.date
        ? new Date(data.userDetails.professionalDetails?.selfEmploymentNetIncome.date)
        : undefined,
      selfEmploymentNetIncomeValue: data.userDetails.professionalDetails?.selfEmploymentNetIncome?.value.amount
        ? String(data.userDetails.professionalDetails.selfEmploymentNetIncome.value.amount)
        : undefined,
      professionalPosition: data.userDetails.professionalDetails?.professionalPosition,
      companyActivity: data.userDetails.professionalDetails?.companyInfo?.activity,
      companyName: data.userDetails.professionalDetails?.companyInfo?.name,
      companyCity: data.userDetails.professionalDetails?.companyInfo?.city,
      seniority: seniority ? seniorityDateFormatter.monthsAndYearsToDate(seniority.months, seniority.years) : undefined,
      contractDateEnd: data.userDetails.professionalDetails?.companyInfo?.contract?.dateEnd
        ? new Date(data.userDetails.professionalDetails?.companyInfo?.contract?.dateEnd)
        : undefined,

      bankAccountNumber: data.income?.accountNumber ? ibanLib.printFormat(data.income?.accountNumber) : '',
      netIncomeType: data.income?.netIncome.type,
      monthlyNetIncome: String(data.income?.netIncome.monthly.amount),
      numberOfYearlyPayments: data.income?.netIncome.annual.amount
        ? data.income?.netIncome.annual.amount / Number(data.income?.netIncome.monthly.amount)
        : undefined,
      ...(data.nieData ? mapNieDataToFormValues(data.nieData) : NIE_DATA_FORM_BLANK_VALUES),
    };
  }

  static fromLoanOnboardingDetailsToForm(
    data: BorrowerOnboardingDetails,
    income: Income | null = null,
    accountNumber: string | null = null,
  ): BorrowerOnboardingDetailsFormType | null {
    if (!data.userDetails || !data.invitation) {
      return null;
    }

    const seniority = data.userDetails.professionalDetails?.companyInfo?.seniorityMonths
      ? {
          months: data.userDetails.professionalDetails.companyInfo.seniorityMonths,
          years: data.userDetails.professionalDetails.companyInfo.seniorityYears,
        }
      : undefined;

    const phoneNumber = data.invitation.phoneNumber ? new PhoneNumber(data.invitation.phoneNumber) : undefined;

    return {
      email: data.invitation.email as string,
      dialCode: getDialCodeItemFromList(phoneNumber?.countryCallingCode) ?? EmptyDialCodeOption,
      phoneNumber: phoneNumber?.nationalNumber ?? '',
      relation: data.invitation.relation ?? undefined,
      documentType: data.userDetails.documentId.documentType,
      documentExpeditor: data.userDetails.documentId.documentExpeditor,
      nationality: data.userDetails.documentId.nationality,
      expiration: new Date(data.userDetails.documentId.expiration),
      documentNumber: data.userDetails.documentId.documentNumber,
      documentAddress: data.userDetails.documentId.documentAddress
        ? AddressMap.toForm(data.userDetails.documentId.documentAddress)
        : ADDRESS_BLANK_VALUES,
      firstName: data.userDetails.documentId.fullName.firstName,
      lastName: data.userDetails.documentId.fullName.lastName,
      birthDate: new Date(data.userDetails.documentId.birthDate),
      gender: data.userDetails.documentId.gender,
      birthPlace: data.userDetails.documentId.birthPlace,
      maritalStatus: data.userDetails.familiarSituationDetails.maritalSituation.maritalStatus,
      maritalRegime: data.userDetails.familiarSituationDetails.maritalSituation.maritalRegime,
      childSupportNumber: data.userDetails.familiarSituationDetails.childSupportNumber ?? null,
      ownershipType: data.userDetails.mainResidenceDetails.ownershipDetails.ownershipType,
      propertyType: data.userDetails.mainResidenceDetails.ownershipDetails.propertyType,
      mortgage: data.userDetails.mainResidenceDetails.ownershipDetails.mortgage === true ? 'true' : 'false',
      homeExpenses: String(data.userDetails.mainResidenceDetails.homeExpenses.amount),
      entranceDate: data.userDetails.mainResidenceDetails.entranceDate
        ? new Date(data.userDetails.mainResidenceDetails.entranceDate)
        : null,
      mainResidenceAddress: AddressMap.toForm(data.userDetails.mainResidenceDetails.address),
      employmentStatus: data.userDetails.professionalDetails?.employmentStatus,
      corporateIdNumber: data.userDetails.professionalDetails?.companyInfo?.corporateIdNumber ?? undefined,
      selfEmploymentNetIncomeDate: data.userDetails.professionalDetails?.selfEmploymentNetIncome?.date
        ? new Date(data.userDetails.professionalDetails?.selfEmploymentNetIncome.date)
        : undefined,
      selfEmploymentNetIncomeValue: data.userDetails.professionalDetails?.selfEmploymentNetIncome?.value.amount
        ? String(data.userDetails.professionalDetails.selfEmploymentNetIncome.value.amount)
        : undefined,
      professionalPosition: data.userDetails.professionalDetails?.professionalPosition,
      companyActivity: data.userDetails.professionalDetails?.companyInfo?.activity,
      companyName: data.userDetails.professionalDetails?.companyInfo?.name,
      companyCity: data.userDetails.professionalDetails?.companyInfo?.city,
      seniority: seniority ? seniorityDateFormatter.monthsAndYearsToDate(seniority.months, seniority.years) : undefined,
      contractDateEnd: data.userDetails.professionalDetails?.companyInfo?.contractDateEnd
        ? new Date(data.userDetails.professionalDetails?.companyInfo?.contractDateEnd)
        : undefined,
      bankAccountNumber: accountNumber ? ibanLib.printFormat(accountNumber) : '',
      netIncomeType: income ? income.netIncome.netIncomeType : undefined,
      monthlyNetIncome: income ? String(income.netIncome.monthlyNetIncome.amount) : '',
      numberOfYearlyPayments: income
        ? income.netIncome.annualNetIncome.amount / Number(income.netIncome.monthlyNetIncome.amount)
        : undefined,
      ...(data.nieData ? mapNieDataToFormValues(data.nieData) : NIE_DATA_FORM_BLANK_VALUES),
    };
  }

  // FIXME: why do we do this? aren't the default values already defined in the schema / each component?
  static getBlankForm(customer?: CheckoutSession['customer']): BorrowerOnboardingDetailsFormType {
    const phoneNumber = customer?.phoneNumber ? new PhoneNumber(customer.phoneNumber) : undefined;
    return {
      // contactData
      email: customer?.email ?? '',
      dialCode: phoneNumber ? getDialCodeItemFromList(phoneNumber.countryCallingCode) : EmptyDialCodeOption,
      phoneNumber: phoneNumber?.nationalNumber ?? '',
      relation: undefined,
      // documentId
      firstName: customer?.fullName.firstName ?? '',
      lastName: customer?.fullName.lastName ?? '',
      documentType: undefined,
      documentNumber: customer?.documentNumber ?? '',
      gender: undefined,
      documentExpeditor: undefined,
      nationality: undefined,
      expiration: null,
      birthDate: null,
      birthPlace: '',
      documentAddress: ADDRESS_BLANK_VALUES,
      // mainResidence
      ownershipType: undefined,
      propertyType: undefined,
      homeExpenses: '',
      entranceDate: null,
      mortgage: '',
      mainResidenceAddress: ADDRESS_BLANK_VALUES,
      // familiarSituation
      maritalStatus: undefined,
      maritalRegime: undefined,
      childSupportNumber: null,
      // professionalDetails
      employmentStatus: undefined,
      professionalPosition: undefined,
      companyName: '',
      companyActivity: undefined,
      companyCity: '',
      seniority: null,
      contractDateEnd: null,
      corporateIdNumber: undefined,
      selfEmploymentNetIncomeDate: null,
      selfEmploymentNetIncomeValue: '',
      // netIncome
      monthlyNetIncome: '',
      numberOfYearlyPayments: undefined,
      netIncomeType: undefined,
      bankAccountNumber: '',
      ...NIE_DATA_FORM_BLANK_VALUES,
    };
  }

  static toContactDataForm(customerData: CheckoutSessionCustomerData): Partial<BorrowerOnboardingDetailsToForm> {
    const phoneNumber = customerData.phoneNumber ? new PhoneNumber(customerData.phoneNumber) : undefined;

    return {
      firstName: customerData.firstName,
      lastName: customerData.lastName,
      email: customerData.email,
      dialCode: phoneNumber ? getDialCodeItemFromList(phoneNumber.countryCallingCode) : EmptyDialCodeOption,
      phoneNumber: phoneNumber?.nationalNumber,
      documentNumber: customerData.documentNumber,
    };
  }
}

export const getSelfEmploymentNetIncomeYear = () => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  return currentMonth >= 1 && currentMonth <= 6 ? currentYear - 2 : currentYear - 1;
};

type BorrowerOnboardingDetailsToForm = Omit<
  BorrowerOnboardingDetailsFormType,
  'bankAccountNumber' | 'netIncomeType' | 'numberOfYearlyPayments'
> & {
  bankAccountNumber?: string;
  netIncomeType?: NetIncomeType;
  numberOfYearlyPayments?: number;
};
